import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { FileText, Ship, Calculator, CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

function RPANode({
  icon: Icon, title, isActive, isComplete, children, bubbleText, showBubble,
}: {
  icon: React.ElementType; title: string; isActive: boolean; isComplete: boolean; children?: React.ReactNode; bubbleText?: string; showBubble?: boolean;
}) {
  return (
    <motion.div
      animate={{
        borderColor: isActive ? "rgba(74, 222, 128, 0.8)" : isComplete ? "rgba(74, 222, 128, 0.4)" : "rgba(71, 85, 105, 0.3)",
        boxShadow: isActive ? "0 0 20px rgba(74, 222, 128, 0.3)" : "none",
      }}
      transition={{ duration: 0.3 }}
      className="relative flex-1 p-3 rounded-xl bg-slate-800/60 backdrop-blur-md border-2 min-h-[180px] flex flex-col"
    >
      {isActive && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute inset-0 rounded-xl bg-emerald-500/10 pointer-events-none" />}

      <div className="flex items-center justify-between mb-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive ? "bg-emerald-500/30" : isComplete ? "bg-emerald-500/20" : "bg-slate-700/50"}`}>
          <Icon className={`w-3.5 h-3.5 ${isActive || isComplete ? "text-emerald-400" : "text-slate-400"}`} />
        </div>
        <AnimatePresence>
          {isComplete && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}><CheckCircle className="w-4 h-4 text-emerald-400" /></motion.div>}
        </AnimatePresence>
      </div>

      <span className={`text-xs font-medium mb-2 ${isActive || isComplete ? "text-emerald-300" : "text-slate-300"}`}>{title}</span>
      <div className="relative flex-1 min-h-[60px]">{children}</div>

      <AnimatePresence>
        {showBubble && bubbleText && (
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -5, scale: 0.95 }} className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap z-20">
            <div className="px-2 py-1 text-[10px] font-medium text-emerald-300 bg-emerald-500/20 rounded-lg border border-emerald-400/30 backdrop-blur-xs">
              {bubbleText}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-emerald-500/20 border-r border-b border-emerald-400/30 rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function FlowArrow({ isActive }: { isActive: boolean }) {
  return (
    <div className="relative flex items-center justify-center w-6 h-full self-center">
      <div className="h-0.5 w-full bg-slate-600/50 relative overflow-hidden">
        <AnimatePresence>
          {isActive && (
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: "200%" }}
              transition={{ duration: 0.8, ease: "linear" }}
              className="absolute h-full w-4 rounded-full"
              style={{ background: "linear-gradient(90deg, transparent, rgb(34, 211, 238), transparent)" }}
            />
          )}
        </AnimatePresence>
      </div>
      <div className="absolute right-0">
        <div className="w-0 h-0 border-t-[4px] border-b-[4px] border-l-[6px] border-t-transparent border-b-transparent" style={{ borderLeftColor: isActive ? "rgb(34, 211, 238)" : "rgb(100, 116, 139)" }} />
      </div>
    </div>
  );
}

function BillSlider({ isActive }: { isActive: boolean }) {
  const { t } = useLanguage();
  const [count, setCount] = useState(0);
  const [currentBill, setCurrentBill] = useState(0);
  const bills = ["MSKU-2024-0086", "COSCO-2024-1234", "MSC-2024-5678", "OOCL-2024-9012"];

  useEffect(() => {
    if (!isActive) { setCount(0); setCurrentBill(0); return; }
    const countInterval = setInterval(() => setCount(prev => Math.min(prev + 7, 86)), 100);
    const billInterval = setInterval(() => setCurrentBill(prev => (prev + 1) % bills.length), 500);
    return () => { clearInterval(countInterval); clearInterval(billInterval); };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="space-y-1.5">
      <div className="relative h-6 overflow-hidden rounded bg-slate-700/30">
        <AnimatePresence mode="wait">
          <motion.div key={currentBill} initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -60, opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-0 flex items-center px-1.5">
            <FileText className="w-2.5 h-2.5 text-emerald-400 mr-1" />
            <span className="text-[10px] text-slate-300 font-mono truncate">{bills[currentBill]}</span>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="text-[10px] text-emerald-400/80">{t("ai.rpaCard.entered")}<span className="font-mono font-bold">{count}</span>{t("ai.rpaCard.tickets")}</div>
    </div>
  );
}

function CabinSync({ isActive }: { isActive: boolean }) {
  const { t } = useLanguage();
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    if (!isActive) { setSynced(false); return; }
    const timer = setTimeout(() => setSynced(true), 1500);
    return () => clearTimeout(timer);
  }, [isActive]);

  if (!isActive && !synced) return null;

  return (
    <div className="space-y-1.5">
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }} className="flex flex-col gap-0.5 p-1.5 bg-slate-700/30 rounded">
        <div className="flex items-center gap-1">
          <Ship className="w-2.5 h-2.5 text-cyan-400" />
          <span className="text-[10px] text-slate-300">Maersk AE1</span>
        </div>
        <span className="text-[10px] text-cyan-400 font-mono">32TEU</span>
      </motion.div>
      {synced && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] text-emerald-400/80 flex items-center gap-1">
          <CheckCircle className="w-2.5 h-2.5" />
          {t("ai.rpaCard.locked")}
        </motion.div>
      )}
    </div>
  );
}

function FeeReconciliation({ isActive }: { isActive: boolean }) {
  const { t } = useLanguage();
  const [matched, setMatched] = useState(false);

  useEffect(() => {
    if (!isActive) { setMatched(false); return; }
    const timer = setTimeout(() => setMatched(true), 1200);
    return () => clearTimeout(timer);
  }, [isActive]);

  if (!isActive && !matched) return null;

  return (
    <div className="space-y-1.5">
      <div className="space-y-0.5">
        <motion.div initial={{ x: -15, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center justify-between text-[10px]">
          <span className="text-slate-400">{t("ai.rpaCard.order")}</span>
          <span className="text-slate-300 font-mono">¥12,580</span>
        </motion.div>
        <motion.div initial={{ x: 15, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="flex items-center justify-between text-[10px]">
          <span className="text-slate-400">{t("ai.rpaCard.misc")}</span>
          <span className="text-slate-300 font-mono">¥12,580</span>
        </motion.div>
      </div>
      {matched && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center justify-center gap-1 p-1 bg-emerald-500/10 rounded border border-emerald-500/30">
          <span className="text-[10px] text-emerald-400">{t("ai.rpaCard.diff")}</span>
          <CheckCircle className="w-2.5 h-2.5 text-emerald-400" />
        </motion.div>
      )}
    </div>
  );
}

export function RPAAutomationCard() {
  const { t } = useLanguage();
  const [phase, setPhase] = useState(0);
  const [showFinalGlow, setShowFinalGlow] = useState(false);

  useEffect(() => {
    const sequence = [
      { phase: 0, duration: 1000 },
      { phase: 1, duration: 3000 },
      { phase: 2, duration: 2500 },
      { phase: 3, duration: 2500 },
      { phase: 4, duration: 1500 },
    ];

    let currentIndex = 0;

    const runSequence = () => {
      const current = sequence[currentIndex]!;
      setPhase(current.phase);
      if (current.phase === 4) {
        setShowFinalGlow(true);
        setTimeout(() => setShowFinalGlow(false), 800);
      }
      currentIndex = (currentIndex + 1) % sequence.length;
      setTimeout(runSequence, current.duration);
    };

    const timer = setTimeout(runSequence, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`relative rounded-2xl bg-gradient-to-br from-slate-900/95 via-slate-800/90 to-slate-900/95 backdrop-blur-xl border border-slate-700/50 overflow-hidden transition-all duration-500 pb-10 ${showFinalGlow ? "shadow-[0_0_40px_rgba(74,222,128,0.2)]" : ""}`}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(74, 222, 128, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(74, 222, 128, 0.2) 1px, transparent 1px)`, backgroundSize: '24px 24px' }} />
      </div>

      <div className="relative z-10 p-5 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }} className="w-3 h-3 rounded-full bg-emerald-400" />
          <h3 className="text-lg font-semibold text-slate-100">{t("ai.rpaCard.title")}</h3>
          <span className="ml-auto text-xs text-slate-500">{t("ai.rpaCard.pipeline")}</span>
        </div>
      </div>

      <div className="relative z-10 p-4 flex gap-2 items-stretch">
        <RPANode icon={FileText} title={t("ai.rpaCard.bill")} isActive={phase === 1} isComplete={phase > 1} bubbleText={t("ai.rpaCard.billBubble")} showBubble={phase === 1 && phase > 0}>
          <BillSlider isActive={phase === 1} />
          {phase > 1 && <div className="text-[10px] text-emerald-400/60">86{t("ai.rpaCard.billDone")}</div>}
        </RPANode>

        <FlowArrow isActive={phase === 1} />

        <RPANode icon={Ship} title={t("ai.rpaCard.cabin")} isActive={phase === 2} isComplete={phase > 2} bubbleText={t("ai.rpaCard.cabinBubble")} showBubble={phase === 2}>
          <CabinSync isActive={phase === 2} />
          {phase > 2 && <div className="text-[10px] text-emerald-400/60">32TEU{t("ai.rpaCard.cabinDone")}</div>}
        </RPANode>

        <FlowArrow isActive={phase === 2} />

        <RPANode icon={Calculator} title={t("ai.rpaCard.fee")} isActive={phase === 3} isComplete={phase > 3} bubbleText={t("ai.rpaCard.feeBubble")} showBubble={phase === 3}>
          <FeeReconciliation isActive={phase === 3} />
          {phase > 3 && <div className="text-[10px] text-emerald-400/60">{t("ai.rpaCard.feeDone")}</div>}
        </RPANode>
      </div>

      <motion.div animate={{ opacity: phase === 4 ? 1 : 0.6 }} className="relative z-10 flex justify-center mt-2 pb-2">
        <div className={`px-4 py-2 rounded-full backdrop-blur-xs border transition-all duration-500 ${phase === 4 ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-300" : "bg-slate-800/50 border-slate-700/50 text-slate-400"}`}>
          <div className="flex items-center gap-2 text-xs">
            {phase === 4 ? (
              <>
                <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.5 }}><CheckCircle className="w-4 h-4" /></motion.div>
                <span>{t("ai.rpaCard.complete")}</span>
              </>
            ) : (
              <>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="w-3 h-3 border-2 border-slate-500 border-t-cyan-400 rounded-full" />
                <span>{t("ai.rpaCard.running")}</span>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
